import { FC, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useGetUser } from 'app/api/hooks/core/v1/auth/user';
import NavIconButton from 'app/components/layout/AppLayout/NavIconButton';
import Box from 'app/components/v2/components/Box';
import { TextProps } from 'app/components/v2/components/Text';
import Heading5 from 'app/components/v2/components/Text/Heading5';
import SmallBody from 'app/components/v2/components/Text/SmallBody';
import Tooltip from 'app/components/v2/components/Tooltip';
import { useAuthState } from 'app/state/AuthContext/hooks';

// Local Typings
interface Props {
  alignment?: 'left' | 'right';
  hideCompanyType?: boolean;
  hideUserInfo?: boolean;
  isOpen?: boolean;
  onIconClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  textColor?: TextProps['textColor'];
  withMarginBottom?: boolean;
}

// Component Definition
const UserInfo: FC<Props> = props => {
  const { t } = useTranslation('components');
  const spanRef = useRef<HTMLSpanElement>(null);
  const user = useGetUser();

  const { companyId } = useAuthState();

  if (!user) {
    return null;
  }

  return (
    <Box
      alignItems="center"
      display="flex"
      justifyContent="flex-end"
      marginBottom={props.withMarginBottom ? 6 : undefined}
    >
      {!props.hideUserInfo && (
        <Box
          alignItems={props.alignment === 'right' ? 'flex-end' : 'flex-start'}
          display="flex"
          flexDirection="column"
        >
          <Tooltip
            childRef={spanRef}
            tooltipLabel={t('layouts.userInfo.companyId', 'Company Id: {{companyId}}', { companyId })}
            tooltipPlacement="top"
          >
            <span ref={spanRef}>
              <Heading5
                alignment={props.alignment}
                textColor={props.textColor}
              >
                {props.hideCompanyType ? user.company?.name : user.company?.displayName}
              </Heading5>
            </span>
          </Tooltip>

          <SmallBody
            as="span"
            textColor={props.textColor}
          >
            {user.name}
          </SmallBody>
        </Box>
      )}

      {props.onIconClick && (
        <NavIconButton
          ariaExpanded={props.isOpen}
          ariaLabel="account sub-menu"
          autoFocus
          buttonColor="icon_button_utility"
          iconName="account-circle"
          marginLeft={2}
          onClick={props.onIconClick}
        />
      )}
    </Box>
  );
};

export default UserInfo;
